import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Save, Plus, Trash2, Globe, MapPin, Phone, Mail, Clock, Briefcase, Users } from 'lucide-react';
import quienesSomosService from '../services/quienesSomosService';

const AdminQuienesSomos = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    nombre_conservador: '',
    link_conservador: '',
    direccion: '',
    telefonos: '',
    email: '',
    horarios: [],
    jurisdiccion: [],
    suplentes: [],
    funcionarios: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await quienesSomosService.get();
      if (res) {
        setData(res);
      }
    } catch (error) {
      toast.error('Error al cargar la información');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await quienesSomosService.update(data);
      toast.success('Información actualizada correctamente');
    } catch (error) {
      toast.error('Error al guardar los cambios');
    } finally {
      setSaving(false);
    }
  };

  const handleListChange = (key, index, field, value) => {
    const list = [...data[key]];
    list[index] = { ...list[index], [field]: value };
    setData({ ...data, [key]: list });
  };

  const addItem = (key, newItem) => {
    setData({ ...data, [key]: [...data[key], newItem] });
  };

  const removeItem = (key, index) => {
    const list = [...data[key]];
    list.splice(index, 1);
    setData({ ...data, [key]: list });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner spinner-lg"></div>
      </div>
    );
  }

  return (
    <div className="container py-8 max-w-5xl">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Administración "Quiénes Somos"</h1>
          <p className="text-gray-400">Edita la información pública del conservador</p>
        </div>
        <button 
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary flex items-center gap-2"
        >
          {saving ? <div className="spinner spinner-sm"></div> : <Save size={18} />}
          {saving ? 'Guardando...' : 'Guardar Cambios'}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Información Básica */}
        <section className="glass-card p-6">
          <div className="flex items-center gap-2 mb-4 text-primary">
            <Globe size={20} />
            <h2 className="text-xl font-semibold">Información General</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="label">Nombre del Conservador</label>
              <input 
                type="text" 
                className="input w-full"
                value={data.nombre_conservador}
                onChange={(e) => setData({...data, nombre_conservador: e.target.value})}
              />
            </div>
            <div>
              <label className="label">Link Web Conservador</label>
              <input 
                type="url" 
                className="input w-full"
                value={data.link_conservador}
                onChange={(e) => setData({...data, link_conservador: e.target.value})}
              />
            </div>
            <div>
              <label className="label">Dirección</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 text-gray-500" size={16} />
                <input 
                  type="text" 
                  className="input w-full pl-10"
                  value={data.direccion}
                  onChange={(e) => setData({...data, direccion: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Teléfonos</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 text-gray-500" size={16} />
                  <input 
                    type="text" 
                    className="input w-full pl-10"
                    value={data.telefonos}
                    onChange={(e) => setData({...data, telefonos: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <label className="label">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 text-gray-500" size={16} />
                  <input 
                    type="email" 
                    className="input w-full pl-10"
                    value={data.email}
                    onChange={(e) => setData({...data, email: e.target.value})}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Horarios */}
        <section className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-primary">
              <Clock size={20} />
              <h2 className="text-xl font-semibold">Horarios de Atención</h2>
            </div>
            <button 
              onClick={() => addItem('horarios', { label: '', time: '' })}
              className="btn btn-sm btn-outline btn-primary p-1"
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="space-y-3">
            {data.horarios.map((h, i) => (
              <div key={i} className="flex gap-2 items-center">
                <input 
                  placeholder="Día/Tipo"
                  className="input flex-1"
                  value={h.label}
                  onChange={(e) => handleListChange('horarios', i, 'label', e.target.value)}
                />
                <input 
                  placeholder="Horas"
                  className="input flex-1"
                  value={h.time}
                  onChange={(e) => handleListChange('horarios', i, 'time', e.target.value)}
                />
                <button onClick={() => removeItem('horarios', i)} className="text-red-400 hover:text-red-300">
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Jurisdicción */}
        <section className="glass-card p-6 md:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-primary">
              <MapPin size={20} />
              <h2 className="text-xl font-semibold">Jurisdicción (Comunas)</h2>
            </div>
            <button 
              onClick={() => addItem('jurisdiccion', '')}
              className="btn btn-sm btn-outline btn-primary p-1"
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {data.jurisdiccion.map((comuna, i) => (
              <div key={i} className="flex gap-2 items-center">
                <input 
                  className="input flex-1"
                  value={comuna}
                  onChange={(e) => {
                    const list = [...data.jurisdiccion];
                    list[i] = e.target.value;
                    setData({ ...data, jurisdiccion: list });
                  }}
                />
                <button onClick={() => removeItem('jurisdiccion', i)} className="text-red-400 hover:text-red-300">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Suplentes */}
        <section className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-primary">
              <Briefcase size={20} />
              <h2 className="text-xl font-semibold">Conservadores Suplentes</h2>
            </div>
            <button 
              onClick={() => addItem('suplentes', { cargo: '', nombre: '' })}
              className="btn btn-sm btn-outline btn-primary p-1"
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="space-y-4">
            {data.suplentes.map((s, i) => (
              <div key={i} className="p-4 bg-white/5 rounded-lg border border-white/10 space-y-3">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1 space-y-3">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1 block">Nombre del Suplente</label>
                      <input 
                        placeholder="Nombre Completo"
                        className="input w-full"
                        value={s.nombre || ''}
                        onChange={(e) => handleListChange('suplentes', i, 'nombre', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1 block">Cargo / Título</label>
                      <input 
                        placeholder="Ej: Abogado"
                        className="input w-full"
                        value={s.cargo || ''}
                        onChange={(e) => handleListChange('suplentes', i, 'cargo', e.target.value)}
                      />
                    </div>
                  </div>
                  <button 
                    onClick={() => removeItem('suplentes', i)} 
                    className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Funcionarios */}
        <section className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-primary">
              <Users size={20} />
              <h2 className="text-xl font-semibold">Oficiales y Funcionarios</h2>
            </div>
            <button 
              onClick={() => addItem('funcionarios', { cargo: '', nombre: '' })}
              className="btn btn-sm btn-outline btn-primary p-1"
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="space-y-4">
            {data.funcionarios.map((f, i) => (
              <div key={i} className="p-4 bg-white/5 rounded-lg border border-white/10 space-y-3">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1 space-y-3">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1 block">Nombre del Funcionario</label>
                      <input 
                        placeholder="Nombre Completo"
                        className="input w-full"
                        value={f.nombre || ''}
                        onChange={(e) => handleListChange('funcionarios', i, 'nombre', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1 block">Cargo / Funciones</label>
                      <textarea 
                        placeholder="Descripción del cargo"
                        className="input w-full min-h-[60px] py-2"
                        value={f.cargo || ''}
                        onChange={(e) => handleListChange('funcionarios', i, 'cargo', e.target.value)}
                      />
                    </div>
                  </div>
                  <button 
                    onClick={() => removeItem('funcionarios', i)} 
                    className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                    title="Eliminar funcionario"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminQuienesSomos;
